import React from 'react';
import {Map, List} from 'immutable';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {shallowEqual} from 'react-recompose';
import {reifySite} from '@packages/systems/core/render';
import {
  createDesignerContext,
  createRenderContext,
  withContentEditorLiveSnapshot,
} from '@packages/systems/render';
import {SharedContext} from '@packages/systems/core/records/SharedContext';
import {addDynamoUtilsToEnv} from '@packages/systems/plugins/DynamoGateway';
import {addBasicImplicitConversionUtilsToEnv} from '@packages/shared/render/plugins/Basic/implicitConversionUtils';
import {singleMemo, singleMemoMulti, weakMemo} from '@packages/utilities/memo';
import {indexById} from '@packages/systems/core/utils/RecordUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {type Ix2NodeMap} from '@packages/shared/utils/ix2NodeUtils';
import {CurrencySettings} from '@packages/systems/core/utils/CurrencyUtils';
import {DynamoData} from '@packages/systems/dynamo/graphql';
import {
  canvasBodyRendered,
  CanvasBodyProps,
  getContextEnvValues,
} from '@packages/systems/designer/canvas';
import {
  replaceComponentCanvasMediaQueries,
  replaceCodeIslandShadowDomMediaQueries,
} from '@packages/systems/designer/open-canvas-mq-rewrite';
import {
  Component,
  type ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  updateComponentRenderInComponentMap,
  pageComponentType,
  getNativeNodeAndElementFromNativeId,
  getComponentRootElement,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {LocaleUtils, selectCurrentLocale} from '@packages/systems/localization';
import type {Locale} from '@packages/systems/localization-types';
import {
  updateEnvForLocalizationActiveLocale,
  getActiveLocale,
} from '@packages/systems/plugins/Localization';
import {TelemetryEventLabel} from '@packages/systems/designer-telemetry';
import {BreakpointID} from '@packages/systems/style/types';
import {
  ContentEditorActive,
  ContentEditorInactive,
  getContentEditorActive,
} from '@packages/shared/states';
import type {
  CustomNodeStateKey,
  PseudoStateKey,
} from '@packages/systems/core/utils/StyleTypes';
import {
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {IXState} from '@packages/systems/designer/ix';
import {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {getComputedStyles} from '@packages/systems/style-legacy/utils';
import {
  ApolloCacheResetStateType,
  ApolloClientStateType,
} from '@packages/systems/designer/apollo-client';
import type {CodeComponentsSafeModeReason} from '@packages/domains/code-libraries/data-types';

import {getOptimizationPluginContext} from '@packages/systems/plugins/Optimization';
import {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import {
  GRID_EDITING_FILTER,
  RenderDesignerCanvas,
} from '@packages/systems/render-designer';
import {getRootElementWithFocusedInstancesExpanded} from '@packages/utilities/platform-selectors';
import {
  ClientLatencyTools,
  DesignerLoadPerformanceTools,
} from '@packages/systems/designer/performance-tools';
import {finishEditToRenderMeasurement} from '../utils/editToRenderTelemetry';
import {finishVariableChangeMeasurement} from '../utils/variableChangeTelemetry';
import {finishMainComponentEditMeasurement} from '../utils/componentEditTelemetry';
import {currentMetricReadyToRenderOutlines} from '@packages/systems/optimization/store';
import type {ValidShippingZonesType} from '@packages/systems/commerce/store';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {DEFAULT_FRAME_ORIENTATION} from '@packages/systems/component-canvas/types';
import {injectGlobalComponentsForComponentCanvas} from '@packages/systems/component-canvas/designer';
import {
  OpenCanvasBodyElement,
  PrimitiveFrame,
  PrimitiveFrameLayout,
  useFrameSizeTransition,
  getBreakpointWidth,
  getFrameDOMElementById,
  useFramePlaceholderHeight,
} from '@packages/systems/open-canvas/ui';
import {OpenCanvas} from '@packages/systems/designer/open-canvas-config';
import {
  rewriteStyleTagCssForOpenCanvas,
  rewriteViewportUnitsInHtml,
} from '@packages/systems/open-canvas/rewrite-viewport-units';
import {
  MCC_ALL_BREAKPOINTS_GAP_PX,
  selectMCCFramesForAllBreakpoints,
  selectMCCCurrentFrameWidth,
  selectMCCRevealedFrameIds,
  selectMCCCustomViewportHeights,
  getMCCViewportHeight,
  type PageFrame,
} from '@packages/systems/multi-context-canvas/store';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import {updateEnvForCurrentPageSettings} from '@packages/systems/page-bindings';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {getBreakpointContainerName} from '@packages/systems/plugins/Frame';
import {
  createModalTargetContextRef,
  createPopoverTargetContextRef,
} from '@packages/systems/overlays';

const getComponentContext = getContextEnvValues(WebflowEnvironments.DESIGNER);

// Setting responsiveImages to false prevents the rendering pipeline
// from adding responsive image attributes to nodes in Designer. Instead,
// in Designer those are handled by ResponsiveImageHelper.
const _reifySite = singleMemoMulti(
  (
    id: string,
    database: string | undefined,
    timezone: string | null,
    shortName: string,
    codeComponentsSafeModeReason:
      | CodeComponentsSafeModeReason
      | null
      | undefined,
    siteBasePath: string | null | undefined
  ) =>
    reifySite(
      {_id: id, database, timezone, shortName, codeComponentsSafeModeReason},
      {siteBasePath}
    ).set('responsiveImages', false)
);

const countStyleBlocks = (element: ExpressionElement | null): number =>
  element ? getStyleBlockIds(element).length : 0;

const getLocalizationContext = singleMemo((locales?: Array<Locale> | null) =>
  singleMemo((lang?: string | null) =>
    singleMemo((pageId?: string | null) =>
      LocaleUtils.getPluginContext({
        locales,
        current: lang,
        pageId,
      })
    )
  )
);

/**
 * @deprecated Don't add new fields to this record, instead use RenderContext.
 */
const sharedContext = singleMemo((hrefMap: Record<string, string>) =>
  singleMemo((pageHref: string) =>
    SharedContext({
      hrefMap,
      pageHref,
    })
  )
);

/**
 * @deprecated Don't add new fields to this object, instead use DesignerContext
 * found in RenderContext
 */
export type DesignerEnvironmentContext = {
  focusedComponentType: Plugins.QualifiedElement;
  focusedSymbolNativeIdPath: string[];
  affectedNodeIds: readonly string[];
  designSiteMode: boolean;
  editSiteMode: boolean;
  buildMode: boolean;
  dropTargets: string[];
  editTargetNativeIdPath: NativeId[] | null | undefined;
  selectedNativeIdPath: string[];
  stopEditingText: () => void;
  pseudoMode: null | PseudoStateKey;
  customNodeState: CustomNodeStateKey | null;
  editingGrid: boolean;
  ix2Targets: any;
  loadedHeadScripts: CanvasBodyProps['loadedHeadScripts'];
  bodyEventHandlers: CanvasBodyProps['bodyEventHandlers'];
  canNavigateFromPageToCMS: boolean;
  isEditingIx2ActionList: boolean;
  saveCollectionItemField: CanvasBodyProps['saveCollectionItemField'];
  contentEditorValidationError: Map<string, Map<string, any>> | null;
  customCodeExecutionAllowed: boolean;
};

/**
 * @deprecated Don't add new fields to this object, instead use DesignerContext
 * found in RenderContext
 */
export const createDesignerEnvironmentContext = (
  env: DesignerEnvironmentContext
) => Map(env);

/**
 * @deprecated Don't add new fields to this record, instead use DesignerContext
 * in RenderContext.
 */
const environmentContext = singleMemoMulti(
  (
    focusedComponentType: Plugins.QualifiedElement,
    focusedSymbolNativeIdPath: string[],
    affectedNodeIds: readonly string[],
    designSiteMode: boolean,
    editSiteMode: boolean,
    buildMode: boolean,
    dropTargets: string[],
    editTargetNativeIdPath: NativeId[] | null | undefined,
    selectedNativeIdPath: string[],
    stopEditingText: () => void,
    pseudoMode: null | PseudoStateKey,
    customNodeState: CustomNodeStateKey | null,
    editingGrid: boolean,
    ix2Targets: Ix2NodeMap,
    loadedHeadScripts: CanvasBodyProps['loadedHeadScripts'],
    bodyEventHandlers: CanvasBodyProps['bodyEventHandlers'],
    canNavigateFromPageToCMS: boolean,
    isEditingIx2ActionList: boolean,
    saveCollectionItemField: CanvasBodyProps['saveCollectionItemField'],
    contentEditorValidationError: Map<string, Map<string, any>> | null,
    customCodeExecutionAllowed: boolean
  ) =>
    createDesignerEnvironmentContext({
      focusedComponentType,
      focusedSymbolNativeIdPath,
      affectedNodeIds,
      designSiteMode,
      editSiteMode,
      buildMode,
      dropTargets,
      editTargetNativeIdPath,
      selectedNativeIdPath,
      stopEditingText,
      pseudoMode,
      customNodeState,
      editingGrid,
      ix2Targets,
      loadedHeadScripts,
      bodyEventHandlers,
      canNavigateFromPageToCMS,
      isEditingIx2ActionList,
      saveCollectionItemField,
      contentEditorValidationError,
      customCodeExecutionAllowed,
    })
);

const dynamo = singleMemoMulti(
  (
    apolloClient: ApolloClientStateType,
    apolloCacheResetCounter: ApolloCacheResetStateType,
    collections: CollectionsType,
    itemId: string | null,
    collectionId: null | string,
    dynamoData: DynamoData | null,
    publicationId: string | null | undefined,
    cmsCollectionListPreviewEnabled: boolean,
    cmsCollectionListPreviewItemsCount: number,
    cmsNestedCollectionListPreviewItemsCount: number
  ) =>
    Map({
      apolloClient,
      apolloCacheResetCounter,
      collections,
      itemId,
      collectionId,
      cachedItems: Map(),
      dynamoData,
      publicationId,
      cmsCollectionListPreviewEnabled,
      cmsCollectionListPreviewItemsCount,
      cmsNestedCollectionListPreviewItemsCount,
    })
);

// @ts-expect-error - TS7006 - Parameter 'list' implicitly has an 'any' type.
const macro = weakMemo((list) => indexById(list));
const widget = singleMemo((googleMapsApiKey: string | null | undefined) =>
  Map({googleMapsApiKey})
);

const form = singleMemo((recaptchaRequired: boolean) =>
  singleMemo((recaptchaSiteKey: string | null | undefined) =>
    Map({recaptchaRequired, recaptchaSiteKey})
  )
);

const search = singleMemo((apolloClient: ApolloClientStateType) =>
  singleMemo((isIndexed: boolean) =>
    singleMemo((action: unknown) => Map({apolloClient, isIndexed, action}))
  )
);
const commerce = singleMemoMulti(
  (
    apolloClient: ApolloClientStateType,
    ecommPublishableKey: string | null,
    ecommShippingZones: ValidShippingZonesType | undefined,
    ecommBusinessAddress: Map<string, string> | undefined,
    ecommCurrencySettings: CurrencySettings | undefined,
    quickCheckoutEnabled: boolean
  ) =>
    Map({
      apolloClient,
      ecommPublishableKey,
      ecommShippingZones,
      ecommBusinessAddress,
      ecommCurrencySettings,
      quickCheckoutEnabled,
    })
);

const pagination = weakMemo(
  (paginationPageNumbers: CanvasBodyProps['paginationPageNumbers']) =>
    Map({pageNumbers: paginationPageNumbers})
);

const pluginsContext = singleMemoMulti(
  (
    apolloClient: ApolloClientStateType,
    apolloCacheResetCounter: ApolloCacheResetStateType,
    itemId: string | null,
    collections: CollectionsType,
    collectionId: null | string,
    ixState: IXState,
    contentEditorState: ContentEditorActive | ContentEditorInactive,
    googleMapsApiKey: string | null | undefined,
    recaptchaRequired: boolean,
    recaptchaSiteKey: string | null | undefined,
    isIndexed: boolean,
    ecommPublishableKey: string | null,
    ecommShippingZones: ValidShippingZonesType | undefined,
    ecommBusinessAddress: Map<string, string> | undefined,
    ecommCurrencySettings: CurrencySettings | undefined,
    quickCheckoutEnabled: boolean,
    paginationPageNumbers: Record<string, number>,
    dynamoData,
    currentLocale: Locale | null | undefined,
    locales: Locale[] | null | undefined,
    pageId: string,
    publicationId: string | null | undefined,
    currentVariationId: string | null,
    cmsCollectionListPreviewEnabled: boolean,
    cmsCollectionListPreviewItemsCount: number,
    cmsNestedCollectionListPreviewItemsCount: number
  ) =>
    Map({
      Dynamo: dynamo(
        apolloClient,
        apolloCacheResetCounter,
        collections,
        itemId,
        collectionId,
        dynamoData,
        publicationId,
        cmsCollectionListPreviewEnabled,
        cmsCollectionListPreviewItemsCount,
        cmsNestedCollectionListPreviewItemsCount
      ),
      macro: macro(ixState.macros ? ixState.macros.toList() : List()),
      Widget: widget(googleMapsApiKey),
      Form: form(recaptchaRequired)(recaptchaSiteKey),
      Search: search(apolloClient)(isIndexed)(undefined),
      Commerce: commerce(
        apolloClient,
        ecommPublishableKey,
        ecommShippingZones,
        ecommBusinessAddress,
        ecommCurrencySettings,
        quickCheckoutEnabled
      ),
      contentEditor: contentEditorState,
      Pagination: pagination(paginationPageNumbers),
      Localization: getLocalizationContext(locales)(currentLocale?.tag)(pageId),
      Optimization: getOptimizationPluginContext(currentVariationId),
    })
);

const contentEditorIsClosing = (
  props: CanvasBodyProps,
  nextProps: CanvasBodyProps
): boolean => {
  return (
    Boolean(getContentEditorActive(props.contentEditorState)) &&
    !getContentEditorActive(nextProps.contentEditorState)
  );
};

const reportCanvasShouldComponentUpdates = (
  shouldComponentUpdate: (nextProps: CanvasBodyProps) => boolean
) => {
  // In production, return the function as-is without any wrapping
  if (process.env.NODE_ENV === 'production') {
    return shouldComponentUpdate;
  }

  // In development, wrap the function to dispatch events when component updates
  return (nextProps: CanvasBodyProps) => {
    const result = shouldComponentUpdate(nextProps);
    if (result) {
      window.dispatchEvent(new Event('CanvasBodyRendered'));
    }
    return result;
  };
};

export default class CanvasBody extends React.Component<CanvasBodyProps> {
  private cleanupCodeIslandObserver: (() => void) | undefined;
  private popoverTargetContextRef = createPopoverTargetContextRef();
  private modalTargetContextRef = createModalTargetContextRef();
  // Builtin `@media` rules disabled in favor of a `@container` equivalent.
  // Restored on Open Canvas deactivation/unmount. `globalThis.Map`, not the
  // Immutable `Map` imported above.
  private disabledBuiltinMediaRules:
    | globalThis.Map<CSSMediaRule, string>
    | undefined;

  shouldComponentUpdate = reportCanvasShouldComponentUpdates(
    (nextProps: CanvasBodyProps) => {
      // Perf HACK: we don't need to update our canvas body when we're making
      // ephemeral style changes, because they will _only_ affect the
      // style sheet.
      //
      // This gives us an "early-out" to avoid some potentially-costly
      // comparisons inside `this__shouldComponentUpdate`:

      // If we're editing content in the Rich Text modal we don't need to update
      // anything:
      if (nextProps.contentEditorState.fromModal) {
        return false;
      }

      // If we just finished editing the subtree from a modal, force the canvas to
      // update:
      if (
        this.props.contentEditorState.fromModal &&
        !nextProps.contentEditorState.fromModal
      ) {
        return true;
      }

      // if the ContentEditor is closing, the canvas should always update
      if (contentEditorIsClosing(this.props, nextProps)) {
        return true;
      }

      if (
        nextProps.isEphemeralChangeInProgress &&
        !nextProps.forceCanvasBodyRerender &&
        // We let the first update go through for local styles to add the `id` attribute to the node being styled
        // (see EvalElement `classNames` function)
        this.props.isEphemeralChangeInProgress
      ) {
        return false;
      }

      return !shallowEqual(nextProps, this.props);
    }
  );

  componentDidMount(): void {
    DesignerLoadPerformanceTools.endPerformanceMark('canvasBodyMounted');
    this.setupOpenCanvasMediaQueryObservers();
  }

  componentWillUnmount(): void {
    this.cleanupCodeIslandObserver?.();
    this.restoreDisabledBuiltinMediaRules();
  }

  private restoreDisabledBuiltinMediaRules() {
    if (!this.disabledBuiltinMediaRules) return;
    for (const [rule, originalMediaText] of this.disabledBuiltinMediaRules) {
      rule.media.mediaText = originalMediaText;
    }
    this.disabledBuiltinMediaRules = undefined;
  }

  private setupOpenCanvasMediaQueryObservers(
    prevBreakpoints?: CanvasBodyProps['breakpoints']
  ) {
    if (this.props.isOpenCanvasActive) {
      if (!this.disabledBuiltinMediaRules) {
        this.disabledBuiltinMediaRules = new globalThis.Map();
      }
      replaceComponentCanvasMediaQueries({
        breakpoints: this.props.breakpoints,
        bodyElementStyleTarget:
          this.props.openCanvasStyleSheetOptions?.bodyElementStyleTarget,
        mut_disabledBuiltinMediaRules: this.disabledBuiltinMediaRules,
      });

      if (
        !this.cleanupCodeIslandObserver ||
        prevBreakpoints !== this.props.breakpoints
      ) {
        this.cleanupCodeIslandObserver?.();
        this.cleanupCodeIslandObserver = replaceCodeIslandShadowDomMediaQueries(
          {
            breakpoints: this.props.breakpoints,
          }
        );
      }
    } else {
      this.restoreDisabledBuiltinMediaRules();
      if (this.cleanupCodeIslandObserver) {
        this.cleanupCodeIslandObserver();
        this.cleanupCodeIslandObserver = undefined;
      }
    }
  }

  componentDidUpdate(prevProps: CanvasBodyProps) {
    // When the component canvas is active, we need to replace any `@media`
    // rules from custom CSS with equivalent `@container` rules — both in
    // document-level stylesheets and inside code-island shadow roots.
    this.setupOpenCanvasMediaQueryObservers(prevProps.breakpoints);

    const nativeId = this.props.selectedNodeNativeId;
    const selectedNodeChanged =
      nativeId && nativeId !== prevProps.selectedNodeNativeId;
    const {
      components,
      focusedComponentType,
      focusedSymbolNativeIdPath,
      variables,
      variableCollections,
      plugins,
      variantState,
      optimizationClickMode,
    } = this.props;

    ClientLatencyTools.finishAndReport(
      TelemetryEventLabel.CANVAS_PASTE_LATENCY
    );
    ClientLatencyTools.finishAndReport(
      TelemetryEventLabel.CLASS_NAME_EDITING_LATENCY
    );
    ClientLatencyTools.finishAndReport(
      TelemetryEventLabel.REMOVE_CANVAS_ELEMENT_LATENCY
    );
    finishEditToRenderMeasurement();
    finishVariableChangeMeasurement();
    finishMainComponentEditMeasurement();

    if (optimizationClickMode) {
      this.props.dispatch(currentMetricReadyToRenderOutlines());
      return;
    }

    const selectedElement = getSelectedElement(
      components,
      focusedComponentType,
      nativeId
    );
    const previousSelectedElement = getSelectedElement(
      prevProps.components,
      prevProps.focusedComponentType,
      prevProps.selectedNodeNativeId
    );

    const selectedNodeStyleBlocksChanged =
      nativeId &&
      !selectedNodeChanged &&
      countStyleBlocks(selectedElement) !==
        countStyleBlocks(previousSelectedElement);

    const selectedNodeIdStylesChanged = Boolean(
      nativeId &&
      !selectedNodeChanged &&
      selectedElement &&
      previousSelectedElement &&
      getElementStyleData(selectedElement, variantState) !==
        getElementStyleData(previousSelectedElement, variantState)
    );

    const selectedPseudoModeChanged =
      this.props.pseudoMode !== prevProps.pseudoMode;

    const selectedVariantChanged =
      this.props.variantState !== prevProps.variantState;

    // Canvas body render action will update computed styles, that are stored on the StyleStore, and are used for setting `browser` style values in the StyleStore.
    // We need to updated computed styles from this component when:
    // 1. The selected node has changed
    // 2. The selected node's style blocks have changed
    // 3. The selected node's Id styles (one set directly on node) have changed
    // 4. The selected pseudo mode (i.e. hover, focus) has changed
    // 5. The selected variant has changed

    // TODO:  Logically we also would need to updated computedStyle on changing breakpoints from here, that should fix known bug when styles are not updated in the style panel on the breakpoint change event,
    // but there is a delay in rendering on changing breakpoints for canvas to render, so the computedStyle is still incorrect. Needs more investigation.
    if (
      selectedNodeChanged ||
      selectedNodeStyleBlocksChanged ||
      selectedNodeIdStylesChanged ||
      selectedPseudoModeChanged ||
      selectedVariantChanged
    ) {
      let payload: {
        computedStyle?: Partial<Record<WebflowCssProperty, string>>;
        selectedNodeIdStylesChanged?: boolean;
      } = {};

      const rootElementWithFocusedInstancesExpanded =
        getRootElementWithFocusedInstancesExpanded(
          components,
          focusedSymbolNativeIdPath
        );

      const currentComponent = components.get(focusedComponentType);

      const {nativeNode} = currentComponent
        ? getNativeNodeAndElementFromNativeId({
            currentComponent,
            focusedSymbolNativeIdPath,
            nativeNodeId: nativeId,
            liveComponentMap: components,
            rootElementWithFocusedInstancesExpanded,
          })
        : {nativeNode: null};

      if (nativeNode) {
        payload = {
          computedStyle: getComputedStyles(
            nativeNode,
            variableCollections,
            [],
            variables,
            Plugins.getPluginsSlugMap(plugins)
          ),
          selectedNodeIdStylesChanged,
        };
      }
      DesignerLoadPerformanceTools.endPerformanceMark('canvasBodyRendered');
      this.props.dispatch(canvasBodyRendered(payload));
      return;
    }

    if (this.props.forceCanvasBodyRerender) {
      DesignerLoadPerformanceTools.endPerformanceMark('canvasBodyRendered');
      this.props.dispatch(canvasBodyRendered({}));
    }
  }

  handleCanvasBodyRender(
    id: string,
    phase: 'mount' | 'update' | 'nested-update',
    actualDuration: number,
    baseDuration: number,
    startTime: number
  ) {
    window.dispatchEvent(
      new CustomEvent('CanvasBodyRendered', {
        detail: {
          id,
          phase,
          actualDuration,
          baseDuration,
          startTime,
        },
      })
    );
  }

  render() {
    const {
      rootExpression,
      currentLocale,

      // shared context props
      classNameMap,
      idAttrMap,
      stylesMap,
      styleBlocks,
      pageId,
      hrefMap,
      assets,
      siteId,
      codeComponentsSafeModeReason,
      databaseId,
      entitlements,
      timezone,
      shortName,
      siteBasePath,
      ix3ElementTargets,
      ix2NodeMap,
      ix2SelectedActionListId,

      // environment context props
      annotationsMode,
      showEmpty,
      showEmptyForBuildModeMap,
      cssPropertyMap,
      focusedComponentType,
      focusedSymbolNativeIdPath,
      affectedNodeIds,
      previewMode,
      designSiteMode,
      editSiteMode,
      buildMode,
      dropTargets,
      editTargetNativeIdPath,
      dispatch,
      selectedNativeIdPath,
      stopEditingText,
      pseudoMode,
      mediaQuery,
      editingGrid,
      loadedHeadScripts,
      bodyEventHandlers,
      customNodeState,
      canNavigateFromPageToCMS,
      saveCollectionItemField,
      contentEditorValidationError,
      customCodeExecutionAllowed,

      // plugins context props
      apolloClient,
      apolloCacheResetCounter,
      currentItemId,
      collections,
      collectionId,
      ixState,
      contentEditorState,
      googleMapsApiKey,
      recaptchaRequired,
      recaptchaSiteKey,
      isIndexed,
      pageHref,
      ecommPublishableKey,
      paginationPageNumbers,
      publicationId,
      collectionIdToPageIdMap,
      ecommShippingZones,
      ecommBusinessAddress,
      quickCheckoutEnabled,
      ecommCurrencySettings,
      dynamoData,

      // Plugins RuntimeContext props
      plugins,
      components,
      variantDataMap,
      variantState,
      optimizationClickMode,
      analyzeMode,
      isOpenCanvasActive,
      frameOrientation,

      isComponentCanvasActive,
      globalComponentIds,
      variables,
      variableCollections,
      omitHiddenElementsInDesigner,
    } = this.props;

    const itemId = currentItemId ?? null;

    const evalEnv = Plugins.getEvalEnv(plugins);

    let env = addDynamoUtilsToEnv(evalEnv, ecommCurrencySettings);
    env = addBasicImplicitConversionUtilsToEnv(env, {
      localizedAssets: assets,
      idAttrMap,
      hrefMap,
      runtimeEnvironment: WebflowEnvironments.DESIGNER,
    });

    const slugMap = Plugins.getPluginsSlugMap(plugins);

    const renderContext = withContentEditorLiveSnapshot(
      createRenderContext(
        stylesMap,
        classNameMap,
        variantDataMap,
        ix3ElementTargets,
        slugMap,
        emptyArray,
        entitlements,
        pageId,
        idAttrMap,
        assets,
        false,
        collectionIdToPageIdMap,
        _reifySite(
          siteId,
          databaseId,
          timezone,
          shortName,
          codeComponentsSafeModeReason,
          siteBasePath
        ),
        createDesignerContext(
          showEmpty,
          previewMode,
          isOpenCanvasActive,
          optimizationClickMode,
          showEmptyForBuildModeMap,
          variantState,
          variables,
          variableCollections,
          cssPropertyMap,
          analyzeMode,
          styleBlocks,
          annotationsMode,
          buildMode,
          false,
          this.props.codeComponentVersion,
          this.props.contentEditorViewportAdapter,
          rewriteViewportUnitsInHtml,
          mediaQuery,
          this.props.contentEditorFrameId,
          this.props.functionsPreview,
          rewriteStyleTagCssForOpenCanvas,
          omitHiddenElementsInDesigner
        ),
        WebflowEnvironments.DESIGNER,
        frameOrientation ?? DEFAULT_FRAME_ORIENTATION,
        null,
        currentLocale || null,
        dispatch,
        emptyObject,
        variableCollections
      ),
      this.props.getContentEditorState
    );
    const sharedContextValue = sharedContext(hrefMap)(pageHref);

    const environmentContextValue = environmentContext(
      focusedComponentType,
      focusedSymbolNativeIdPath,
      affectedNodeIds,
      designSiteMode,
      editSiteMode,
      buildMode,
      dropTargets,
      editTargetNativeIdPath,
      selectedNativeIdPath,
      stopEditingText,
      pseudoMode,
      customNodeState,
      editingGrid,
      ix2NodeMap,
      loadedHeadScripts,
      bodyEventHandlers,
      canNavigateFromPageToCMS,
      Boolean(ix2SelectedActionListId),
      saveCollectionItemField,
      contentEditorValidationError,
      customCodeExecutionAllowed
    );

    const pluginsContextValue = pluginsContext(
      apolloClient,
      apolloCacheResetCounter,
      itemId,
      collections,
      collectionId,
      ixState,
      contentEditorState,
      googleMapsApiKey,
      recaptchaRequired,
      recaptchaSiteKey,
      isIndexed,
      ecommPublishableKey,
      ecommShippingZones,
      ecommBusinessAddress,
      ecommCurrencySettings,
      quickCheckoutEnabled,
      paginationPageNumbers,
      dynamoData,
      this.props.currentLocale,
      this.props.locales,
      pageId,
      publicationId,
      this.props.currentVariationId,
      this.props.cmsCollectionListPreviewEnabled,
      this.props.cmsCollectionListPreviewItemsCount,
      this.props.cmsNestedCollectionListPreviewItemsCount
    );
    env = updateEnvForLocalizationActiveLocale(env)(
      getActiveLocale(pluginsContextValue)
    );

    env = updateEnvForCurrentPageSettings(env)(this.props.currentPageEnvValue);

    // When inside the component canvas with global components configured,
    // a hidden container with an instance of each global component is prepended
    // to the page's Body element.
    const nextRootExpression =
      isComponentCanvasActive && globalComponentIds?.length
        ? injectGlobalComponentsForComponentCanvas(rootExpression)(components)(
            globalComponentIds
          )
        : rootExpression;

    const updatedComponents = updateComponentRenderInComponentMap(
      components,
      pageComponentType,
      nextRootExpression
    );
    this.popoverTargetContextRef.update(updatedComponents, pageComponentType);
    this.modalTargetContextRef.update(updatedComponents, pageComponentType);
    const componentContext = getComponentContext(plugins)(
      this.props.componentLocalizations
    )(this.popoverTargetContextRef.context)(this.modalTargetContextRef.context);

    const renderedContent = (
      <RenderDesignerCanvas
        components={updatedComponents}
        plugins={plugins}
        evalEnv={env}
        componentContext={componentContext}
        sharedContext={sharedContextValue}
        environmentContext={environmentContextValue}
        pluginsContext={pluginsContextValue}
        renderContext={renderContext}
      />
    );

    if (isOpenCanvasActive) {
      return (
        <OpenCanvasBodyElement bodyEventHandlers={bodyEventHandlers}>
          {isComponentCanvasActive ? (
            renderedContent
          ) : (
            <RenderWithFrames editingGrid={editingGrid}>
              {renderedContent}
            </RenderWithFrames>
          )}
        </OpenCanvasBodyElement>
      );
    } else {
      return renderedContent;
    }
  }
}

const RenderWithFrames: React.FC<{
  children: React.ReactNode;
  editingGrid: boolean;
}> = ({children, editingGrid}) => {
  // TODO: This is temporarily just grabbing the active frame id, which is correct
  // for the scenario of a single frame.
  // Eventually, this should be updated to pull data of multiple frames from the store,
  // once we render multiple breakpoints side by side.
  const activeFrameId = useDesignerSelector(OpenCanvas.selectActiveFrameId);

  const allFrames = useDesignerSelector(selectMCCFramesForAllBreakpoints);

  if (!activeFrameId) {
    return <>{children}</>;
  }

  if (allFrames.length > 0) {
    return (
      <RenderWithAllBreakpointFrames
        frames={allFrames}
        activeFrameId={activeFrameId}
        editingGrid={editingGrid}
      >
        {children}
      </RenderWithAllBreakpointFrames>
    );
  }

  return (
    <RenderWithActiveFrame
      activeFrameId={activeFrameId}
      editingGrid={editingGrid}
    >
      {children}
    </RenderWithActiveFrame>
  );
};

const selectCurrentLocaleTag = (state: DesignerStores) =>
  selectCurrentLocale(state.LocalizationStore)?.tag;

/**
 * Returns `rtl` when the Frame is displaying for right-to-left locales.
 * Otherwise returns `undefined`
 */
const useFrameLocaleDir = (): 'rtl' | undefined => {
  const currentLocaleTag = useDesignerSelector(selectCurrentLocaleTag);
  return currentLocaleTag && LocaleUtils.isRTL(currentLocaleTag)
    ? 'rtl'
    : undefined;
};

/**
 * Temporary entrypoint (DEX-643): renders the page content into one
 * `PrimitiveFrame` per breakpoint, side-by-side. Each frame declares its own
 * breakpoint container-name chain (CANV-779), so breakpoint-scoped
 * `@container` queries resolve per frame; the layout stays an unnamed
 * inline-size container.
 */
const RenderWithAllBreakpointFrames: React.FC<{
  children: React.ReactNode;
  frames: PageFrame[];
  activeFrameId: string;
  editingGrid: boolean;
}> = ({children, frames, activeFrameId, editingGrid}) => {
  const layoutWidth = getBreakpointWidth(BREAKPOINT_ID_MAIN);
  const revealedFrameIds = useDesignerSelector(selectMCCRevealedFrameIds);
  const frameDir = useFrameLocaleDir();

  return (
    <PrimitiveFrameLayout
      width={layoutWidth}
      orientation="horizontal"
      gap={MCC_ALL_BREAKPOINTS_GAP_PX}
      filter={editingGrid ? GRID_EDITING_FILTER : undefined}
      style={{alignItems: 'start'}}
    >
      {frames.map((frame) => (
        <BreakpointFrame
          key={frame.id}
          frame={frame}
          isActive={frame.id === activeFrameId}
          isContentRevealed={Boolean(revealedFrameIds[frame.id])}
          dir={frameDir}
        >
          {children}
        </BreakpointFrame>
      ))}
    </PrimitiveFrameLayout>
  );
};

const BreakpointFrame: React.FC<{
  frame: PageFrame;
  children: React.ReactNode;
  isActive: boolean;
  isContentRevealed: boolean;
  dir?: 'rtl';
}> = ({frame, children, isActive, isContentRevealed, dir}) => {
  const frameSizeTransition = useFrameSizeTransition(frame.id);
  const width = frameSizeTransition?.width ?? frame.width;
  const customViewportHeights = useDesignerSelector(
    selectMCCCustomViewportHeights
  );
  const breakpointMinHeight = getMCCViewportHeight(
    frame.breakpointId,
    customViewportHeights
  );

  const hiddenFrameHeight = useFramePlaceholderHeight({
    frameId: frame.id,
    isContentRevealed,
    getFrameDOMElement: getFrameDOMElementById,
  });

  const minHeight = breakpointMinHeight;
  const height = hiddenFrameHeight || 'auto';

  return (
    <PrimitiveFrame
      frameId={frame.id}
      width={width}
      height={height}
      minHeight={minHeight}
      // Only paint the default white body on revealed frames. Hidden frames
      // leave the body transparent so the hidden-content overlay (drawn by
      // `MultiContextCanvasFramesRenderer`) is the sole surface — avoiding
      // the sub-pixel rendering seams that appear when a white frame body
      // shows through the translucent overlay at fractional zoom levels.
      backgroundColor={isContentRevealed ? '#ffffff' : undefined}
      vhBase={minHeight}
      breakpointContainerName={getBreakpointContainerName(frame.breakpointId)}
      data-wf-frame-active={isActive}
      dir={dir}
      style={
        isContentRevealed ? BREAKPOINT_FRAME_STYLE : HIDDEN_CONTENT_FRAME_STYLE
      }
    >
      {/* Frames whose content isn't revealed render as empty frames that
          stretch to the grid height. The placeholder overlay is drawn by
          the frame tools overlay (MultiContextCanvasFramesRenderer), so
          nothing is mounted here. */}
      {isContentRevealed ? (
        <BreakpointFrameContent isActive={isActive}>
          {children}
        </BreakpointFrameContent>
      ) : null}
    </PrimitiveFrame>
  );
};

const BreakpointFrameContent: React.FC<{
  isActive: boolean;
  children: React.ReactNode;
}> = ({isActive, children}) => {
  const deferredChildren = React.useDeferredValue(children);
  return <>{isActive ? children : deferredChildren}</>;
};

// The all-breakpoints frame grid uses `alignItems: 'start'` so frames of
// different breakpoints aren't stretched to the tallest one. That leaves each
// frame's height indefinite (`auto` + minHeight), so the body proxy's
// `min-height: 100%` can't resolve and body-level styles (e.g. a background)
// would stop at the content's height. Making the frame a flex column lets the
// proxy (`flexGrow: 1`) fill the leftover space up to the frame's min-height.
const BREAKPOINT_FRAME_STYLE = {
  display: 'flex',
  flexDirection: 'column',
} as const;

// Hidden frames have no content to size them, so they stretch to the
// layout's runtime height (the tallest revealed frame) instead of the
// layout's `alignItems: 'start'` collapsing them to their (zero) content
// height.
const HIDDEN_CONTENT_FRAME_STYLE = {
  ...BREAKPOINT_FRAME_STYLE,
  alignSelf: 'stretch',
} as const;

const selectCurrentBreakpointId = (state: {
  ExternalStore: {mediaQuery: BreakpointID};
}) => state.ExternalStore.mediaQuery;

const RenderWithActiveFrame: React.FC<{
  children: React.ReactNode;
  activeFrameId: string;
  editingGrid: boolean;
}> = ({children, activeFrameId, editingGrid}) => {
  const frameSizeTransition = useFrameSizeTransition(activeFrameId);
  const frameWidth = useDesignerSelector(selectMCCCurrentFrameWidth);
  const breakpointId = useDesignerSelector(selectCurrentBreakpointId);
  const customViewportHeights = useDesignerSelector(
    selectMCCCustomViewportHeights
  );
  const frameDir = useFrameLocaleDir();
  const width =
    frameSizeTransition?.width ??
    frameWidth ??
    getBreakpointWidth(breakpointId);
  const minHeight = getMCCViewportHeight(breakpointId, customViewportHeights);
  const containerName = getBreakpointContainerName(breakpointId);

  return (
    <PrimitiveFrameLayout
      width={width}
      orientation="vertical"
      gap={0}
      filter={editingGrid ? GRID_EDITING_FILTER : undefined}
    >
      <PrimitiveFrame
        frameId={activeFrameId}
        width={width}
        height="auto"
        minHeight={minHeight}
        backgroundColor="#ffffff"
        vhBase={minHeight}
        breakpointContainerName={containerName}
        dir={frameDir}
      >
        {children}
      </PrimitiveFrame>
    </PrimitiveFrameLayout>
  );
};

function getSelectedElement(
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement,
  selectedNodeNativeId: NativeId | null
): ExpressionElement | null {
  const componentRoot = getComponentRootElement(componentMap, componentType);
  const guid = getGuidNullable(selectedNodeNativeId);
  return componentRoot && guid
    ? Expressions.getElementById(EElement(componentRoot), guid)
    : null;
}

function getElementStyleData(
  element: ExpressionElement,
  variantState: VariantStateKey | null
): Expression | null {
  const path = [
    {in: 'Record', at: 'style'},
    ...(variantState
      ? [
          {in: 'Record', at: 'variants'},
          {in: 'Record', at: variantState},
        ]
      : [{in: 'Record', at: 'base'}]),
  ] as ExpressionPath;
  const elementData = element ? Expressions.getElementData(element) : null;
  const styleData = elementData ? Expressions.getIn(elementData, path) : null;
  return styleData || null;
}

export const forTestSuite = {
  RenderWithAllBreakpointFrames,
  RenderWithActiveFrame,
  selectCurrentLocaleTag,
};
